import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GetSurveyAssignments,
  DownloadSurveyJson,
  GetSurveySubmissionById,
  RevokeSurveyAssignment,
} from "../../../utils/apiCalls/surveyBuilder";
import { useToast } from "../../../utils/ToastContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useConfirmDialog from "../../../hooks/useConfirmDialog";

export default function useSurveyResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // This page is always scoped to one survey, arrived at from the surveys
  // list (see useSurveysList's handleViewResults).
  const surveyId = location.state?.surveyId || "";
  const surveyTitle = location.state?.surveyTitle || "";
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const revokeDialog = useConfirmDialog();

  const fetchResults = useCallback(async (searchString, status, pageIndex, pageSize) => {
    if (!surveyId) return; // safety guard, see redirect effect below
    setIsLoading(true);
    setFetchError("");
    try {
      const res = await GetSurveyAssignments({ surveyId, searchString, status, pageIndex, pageSize });
      if (res.statusCode === 200) {
        setRows(res.data);
        setTotalRows(res.totalRecords ?? res.data.length);
      } else {
        setRows([]);
        setTotalRows(0);
        setFetchError(res.message || "Failed to load survey results");
      }
    } catch (error) {
      setRows([]);
      setTotalRows(0);
      setFetchError(error?.message || "Failed to load survey results");
    }
    setIsLoading(false);
  }, [surveyId]);

  // Opened directly or after a refresh with no router state: surveyId falls
  // back to "", and GetSurveyAssignments would send surveyId: null — the
  // backend rejects that (expects a non-nullable Int64) with a 400. Send the
  // user back to the list instead of surfacing that as a broken results table.
  useEffect(() => {
    if (!surveyId) {
      navigate("/surveys", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchResults(debouncedSearchQuery, statusFilter, page, rowsPerPage);
    setSelectedIds([]);
  }, [fetchResults, debouncedSearchQuery, statusFilter, page, rowsPerPage]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setPage(0);
  };
  const hasActiveFilters = Boolean(searchQuery || statusFilter);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const retryFetch = () => fetchResults(debouncedSearchQuery, statusFilter, page, rowsPerPage);

  const handleView = async (row) => {
    try {
      const res = await GetSurveySubmissionById(row.id);
      if (res.statusCode === 200) {
        sessionStorage.setItem("surveyResultRowData", JSON.stringify(res.data));
        // Carried along so the detail page's back button can return here
        // (not the surveys list) even after a refresh drops router state —
        // GetSurveySubmissionById's response has no surveyId of its own.
        sessionStorage.setItem("surveyResultsContext", JSON.stringify({ surveyId, surveyTitle }));
        navigate("/survey-results-view", { state: { result: res.data, surveyId, surveyTitle } });
      } else {
        showToast(res.message || "Failed to load survey submission", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to load survey submission", "error");
    }
  };

  const handleDownload = async (patientSurveyIds) => {
    try {
      const res = await DownloadSurveyJson(patientSurveyIds);
      if (res.statusCode !== 200) {
        showToast(res.message || "Failed to download survey response(s)", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to download survey response(s)", "error");
    }
  };

  const handleDownloadOne = (row) => handleDownload([row.id]);
  const handleDownloadSelected = () => handleDownload(selectedIds);

  const handleRevokeClick = (row) => revokeDialog.openDialog({ ids: [row.id], label: row.patientName });
  const handleRevokeSelectedClick = () => revokeDialog.openDialog({ ids: selectedIds, label: null });

  const confirmRevoke = async () => {
    const { ids } = revokeDialog.target || {};
    if (!ids?.length) {
      revokeDialog.closeDialog();
      return;
    }
    try {
      const res = await RevokeSurveyAssignment(ids);
      if (res.statusCode === 200) {
        showToast("Survey access revoked", "success");
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        retryFetch();
      } else {
        showToast(res.message || "Failed to revoke survey access", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to revoke survey access", "error");
    }
    revokeDialog.closeDialog();
  };

  const toggleSelectRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === rows.length ? [] : rows.map((r) => r.id)));
  };

  return {
    surveyTitle,
    searchQuery,
    statusFilter,
    rows,
    page,
    rowsPerPage,
    totalRows,
    selectedIds,
    isLoading,
    fetchError,
    hasActiveFilters,
    handleSearchChange,
    handleStatusFilterChange,
    handleClearFilters,
    handleChangePage,
    handleChangeRowsPerPage,
    retryFetch,
    handleView,
    handleDownloadOne,
    handleDownloadSelected,
    revokeDialog,
    handleRevokeClick,
    handleRevokeSelectedClick,
    confirmRevoke,
    toggleSelectRow,
    toggleSelectAll,
  };
}
